import { useState } from 'react'
import { ActionIcon } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import type { ChecklistStep } from '@/pages/Boards/hooks'
import { CREATE_TASK, SECTION_LABEL_STYLE } from './createTaskTheme'

type Props = {
  steps: ChecklistStep[]
  onChange: (steps: ChecklistStep[]) => void
}

/**
 * Checklist composed before the task exists: the steps live in the caller's
 * state and are written once, by the create modal's `boardItemUpdate`. The
 * board's own ChecklistSection can't serve here — it saves through the item it
 * is bound to.
 *
 * No per-step done toggle: every step of a task being filed starts undone, and
 * a checkbox that only ever means "not yet" is noise. Steps are ticked off on
 * the board, where the work actually happens.
 */
export const ChecklistBuilder = ({ steps, onChange }: Props) => {
  const [title, setTitle] = useState('')

  const add = () => {
    const trimmed = title.trim()
    if (!trimmed) return
    onChange([...steps, { id: crypto.randomUUID(), title: trimmed, done: false }])
    setTitle('')
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div style={SECTION_LABEL_STYLE}>Checklist</div>
      <div className="flex flex-col gap-0.5">
        {steps.map((step) => (
          <div
            key={step.id}
            className="flex items-center gap-2.5 px-2.5 py-2"
            style={{ borderRadius: 9, background: CREATE_TASK.railBg }}
          >
            <span className="grow" style={{ fontSize: 14, color: CREATE_TASK.body }}>
              {step.title}
            </span>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Remove step: ${step.title}`}
              onClick={() => onChange(steps.filter((other) => other.id !== step.id))}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </div>
        ))}
        <div className="flex items-center gap-2.5 px-2.5 py-2">
          <span style={{ fontSize: 15, lineHeight: 1, color: '#A9B6C1' }}>+</span>
          <input
            type="text"
            aria-label="Add a step"
            placeholder="Add a step"
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              add()
            }}
            onBlur={add}
            className="h-[26px] grow border-none bg-transparent outline-none"
            style={{ fontSize: 14, color: CREATE_TASK.ink }}
          />
        </div>
      </div>
    </div>
  )
}
